import React from "react";
import { FieldCaption } from "@rebasepro/cms";
import { ExternalLinkIcon, TextField, Typography } from "@rebasepro/ui";

/**
 * The design system's "smallest", in pixels.
 *
 * `ExternalLinkIcon` is a Lucide icon re-export, so `size` is a NUMBER — the
 * `IconSize` strings ("smallest", "small") belong to the wrapped `Icon`
 * component and go straight through to the SVG here. `size="smallest"` renders
 * `width="smallest" height="smallest"`, which is not a length: the browser
 * ignores both and the icon comes out at whatever the viewBox implies. The
 * value is 16 in `@rebasepro/ui`'s own `iconSize` map, which is not re-exported
 * from the package root — hence the literal, and hence this note.
 */
const ICON_PX = 16;

/**
 * A URL you can actually follow, in the form and in the table.
 *
 * ── Why this is here and not in the framework ────────────────────────────────
 * `url: true` on a property is the DATA statement — this string is a URI, and
 * the OpenAPI contract is generated from it. The admin at 0.19.1 keys its link
 * rendering off `admin.urlPreview` instead, so a property that had correctly
 * declared itself a URL still rendered as plain text you had to select and copy.
 * Every URL in this app: the signal you want to open, the company's site, the
 * page an assistant quoted.
 *
 * The framework is the right place for that and is fixed there, but the fix
 * arrives with a release, and this app runs published packages on purpose — a
 * build here is the build the tenant gets. So the same behaviour is assembled
 * from the parts 0.19.1 already exports: `TextField` takes an `endAdornment`,
 * and `FieldCaption` draws the description and the error exactly as every other
 * field does. Nothing here reimplements a control; it decorates one.
 *
 * Delete this file when the admin renders `url: true` on its own — and until
 * then, note that `AdminApp` attaches it by *reading the property*, not by
 * listing field names, so a new URL column is covered the moment it declares
 * itself one.
 */

/**
 * A bare hostname: `junip.co`, `mail.acme.co.uk`.
 *
 * Deliberately strict. This is the fallback for a value with no scheme, so it
 * decides whether arbitrary text becomes a clickable link — and "any string with
 * a dot in it" would turn a filename, a version number and the end of a sentence
 * into one. Labels of letters, digits and hyphens; a final label of at least two
 * letters; no spaces, no slash, no `@`.
 */
const HOSTNAME_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/i;

/**
 * The href this value may become, or nothing.
 *
 * Only `http` and `https` survive. These values arrive from scraped pages and
 * from a model — from strangers — and `javascript:` in an `href` is the oldest
 * trick there is.
 *
 * A value with no scheme at all gets `https://` if it is a hostname. That is
 * what `companies.domain` holds: `junip.co` is a web address a person wants to
 * click, and it is NOT a URI — which is why the property does not and should
 * not declare `url: true`, since that is what the OpenAPI contract's
 * `format: uri` is generated from. A bare domain is linkable without being a
 * URL, and those are two different claims.
 */
export function safeHref(value: unknown, bareHost = false): string | undefined {
    const text = typeof value === "string" ? value.trim() : "";
    if (!text) return undefined;

    try {
        const url = new URL(text);
        return url.protocol === "http:" || url.protocol === "https:" ? url.href : undefined;
    } catch {
        // No scheme. Only a property that holds a HOST may fall back, and that
        // restriction is the whole safety of it: `.ts` is Tonga, so no pattern
        // can tell `schema.generated.ts` from a filename. The property saying
        // "this column is a domain" is the thing that can.
        if (!bareHost || !HOSTNAME_RE.test(text)) return undefined;
        try {
            // Parsed rather than concatenated: `new URL` decides whether the
            // result is addressable, and normalises the case and the trailing
            // slash the way the link will actually resolve.
            const url = new URL(`https://${text}`);
            return url.hostname ? url.href : undefined;
        } catch {
            return undefined;
        }
    }
}

/**
 * Whether this property holds a bare host rather than a full address.
 *
 * A property that declares `url: true` holds a URI and needs no guessing.
 * Anything else this field is attached to was attached *because* it is a
 * domain column — see `AdminApp`'s `withUrlLinks`.
 */
function holdsBareHost(property: { url?: boolean } | undefined): boolean {
    return property?.url !== true;
}

interface UrlFieldProps {
    propertyKey: string;
    value: string | null;
    setValue: (value: string | null) => void;
    error?: unknown;
    showError?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    hideLabel?: boolean;
    includeDescription?: boolean;
    size?: "smallest" | "small" | "medium" | "large";
    property: { name?: string; description?: string; url?: boolean; validation?: { required?: boolean } };
}

/** The editing control: the same text field, with a way out of it. */
export function UrlField({
    propertyKey,
    value,
    setValue,
    error,
    showError,
    disabled,
    autoFocus,
    hideLabel,
    includeDescription,
    size = "small",
    property
}: UrlFieldProps) {
    const href = safeHref(value, holdsBareHost(property));

    return (
        <>
            <TextField
                type="url"
                value={value ?? ""}
                onChange={event => setValue((event.target as HTMLInputElement).value || null)}
                label={hideLabel ? undefined : (property.name ?? propertyKey)}
                disabled={disabled}
                autoFocus={autoFocus}
                error={Boolean(showError && error)}
                size={size}
                endAdornment={href
                    ? (
                        <a
                            href={href}
                            target="_blank"
                            // `noopener` because a page opened from an admin
                            // must not get a handle on it; `noreferrer` because
                            // the URL of this panel is not a stranger's
                            // business; `nofollow` because a link out of a
                            // scraped page is not an endorsement.
                            rel="noopener noreferrer nofollow"
                            title={`Open ${href}`}
                            aria-label={`Open ${property.name ?? propertyKey} in a new tab`}
                            className="flex items-center px-1 text-surface-accent-500 hover:text-primary"
                            // The adornment sits inside the field's click area,
                            // which focuses the input. Without this, following
                            // the link also puts a cursor in the box.
                            onMouseDown={event => event.stopPropagation()}
                        >
                            <ExternalLinkIcon size={ICON_PX}/>
                        </a>
                    )
                    : undefined}
            />
            {includeDescription !== false && (
                <FieldCaption error={Boolean(showError && error)}>
                    {showError && error ? String(error) : property.description}
                </FieldCaption>
            )}
        </>
    );
}

/**
 * The read-only rendering: a table cell, a card line, a reference chip.
 *
 * Truncated rather than wrapped — a URL is long, a row is one line, and the
 * title attribute carries the whole of it for anyone who wants to read it
 * before clicking.
 */
export function UrlPreview({ value, property }: { value: unknown; property?: { url?: boolean } }) {
    const href = safeHref(value, holdsBareHost(property));
    const text = typeof value === "string" ? value : "";
    if (!text) return null;
    if (!href) return <Typography variant="body2" className="mb-0">{text}</Typography>;

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            title={href}
            onClick={event => event.stopPropagation()}
            className="inline-flex items-center gap-1.5 max-w-full text-primary hover:underline"
        >
            <ExternalLinkIcon size={ICON_PX}/>
            <span className="truncate">{text}</span>
        </a>
    );
}
